import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { toast, Toaster } from 'sonner';
import { apiCall } from '../config/api';
import { supabase } from '../config/supabase';
import {
  Fish,
  Camera,
  ChevronRight,
  Calendar,
  MapPin,
  CheckCircle,
  ArrowRight,
  Eye,
  Monitor,
  Cpu,
  BarChart3,
  Shield,
  TrendingUp,
  Gauge,
  Activity
} from 'lucide-react';

// Form validation schema
const contactFormSchema = z.object({
  nombre: z.string().min(2, { message: "Nombre requerido" }),
  cargo: z.string().min(2, { message: "Cargo requerido" }),
  empresa: z.string().min(2, { message: "Nombre de empresa requerido" }),
  correo: z.string().email({ message: "Email válido requerido" }),
  numero: z.string().optional(),
  descripcion: z.string().optional()
});

const AquaSur2025 = () => {
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const form = useForm({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      nombre: '',
      cargo: '',
      empresa: '',
      correo: '',
      numero: '',
      descripcion: ''
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: Eye,
      title: "Detección Precisa",
      description: "Algoritmos de deep learning con precisión superior al 98% en conteo individual."
    },
    {
      icon: Gauge,
      title: "Alta Velocidad",
      description: "Procesamiento de más de 60 peces por segundo en tiempo real."
    },
    {
      icon: BarChart3,
      title: "Análisis de Datos",
      description: "Dashboard con reportes automáticos, históricos y exportación de datos."
    },
    {
      icon: Monitor,
      title: "Integración Simple",
      description: "APIs REST/MQTT compatibles con sistemas de gestión existentes."
    },
    {
      icon: Shield,
      title: "Hardware Industrial",
      description: "Equipos IP67 diseñados para ambientes de producción exigentes."
    },
    {
      icon: TrendingUp,
      title: "Estimación de Biomasa",
      description: "Medición no invasiva de tamaño y peso promedio de los peces."
    }
  ];

  const onSubmit = async (data) => {
    setIsFormSubmitting(true);
    const loadingToast = toast.loading("Enviando mensaje...");

    try {
      const currentDate = new Date().toISOString();
      const contactData = {
        name: data.nombre,
        email: data.correo,
        phone: data.numero || null,
        company: data.empresa,
        subject: `[AQUASUR] ${data.nombre} - ${data.cargo}`,
        message: `CONTACTO AQUASUR 2026 - FABRIMAQ:\n\n- Nombre: ${data.nombre}\n- Cargo: ${data.cargo}\n- Empresa: ${data.empresa}\n- Email: ${data.correo}${data.numero ? `\n- Teléfono: ${data.numero}` : ''}${data.descripcion ? `\n\nMensaje:\n${data.descripcion}` : ''}\n\nFecha: ${currentDate}`,
        status: 'aquasur_contact'
      };

      if (window.location.hostname === 'localhost') {
        try {
          await apiCall('/api/aquasur-contact', {
            method: 'POST',
            body: JSON.stringify(data)
          });
        } catch {
          const { error } = await supabase.from('contacts').insert([contactData]).select();
          if (error) throw error;
        }
      } else {
        const { error } = await supabase.from('contacts').insert([contactData]).select();
        if (error) throw error;
      }

      toast.dismiss(loadingToast);
      toast.success("Mensaje enviado", {
        description: "Nos pondremos en contacto contigo pronto."
      });
      form.reset();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Error al enviar", {
        description: "Por favor intenta nuevamente."
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className={`transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="container mx-auto px-6 py-20 lg:py-32">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 text-sm text-neutral-500 mb-8">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Marzo 2026
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Puerto Montt, Chile
              </span>
            </div>

            <p className="text-neutral-500 text-lg mb-4">Fabrimaq en AquaSur 2026</p>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-neutral-900 leading-tight mb-6">
              Sistema de Conteo de Peces con Visión por Computador
            </h1>

            <p className="text-xl text-neutral-600 mb-10 max-w-2xl">
              Tecnología de inteligencia artificial desarrollada para la industria salmonera.
              Conteo preciso, automatizado y en tiempo real.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-neutral-900 hover:bg-neutral-800 text-white px-8"
                onClick={() => document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' })}
              >
                Solicitar información
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-neutral-300 text-neutral-700 hover:bg-neutral-50 px-8"
                onClick={() => document.getElementById('producto').scrollIntoView({ behavior: 'smooth' })}
              >
                Conocer el producto
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-neutral-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-neutral-200">
            {[
              { value: ">98%", label: "Precisión" },
              { value: "60+", label: "Peces/segundo" },
              { value: "24/7", label: "Operación continua" },
              { value: "IP67", label: "Certificación" }
            ].map((stat, index) => (
              <div key={index} className="py-10 px-6 text-center">
                <p className="text-3xl font-semibold text-neutral-900">{stat.value}</p>
                <p className="text-sm text-neutral-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Features */}
      <section id="producto" className="py-20 lg:py-28">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">
              Características del sistema
            </h2>
            <p className="text-neutral-600">
              Solución integral de visión por computador diseñada específicamente
              para las necesidades de la industria acuícola.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="group">
                  <div className="p-3 bg-neutral-100 rounded-lg w-fit mb-4 group-hover:bg-neutral-200 transition-colors">
                    <IconComponent className="h-5 w-5 text-neutral-700" />
                  </div>
                  <h3 className="text-lg font-medium text-neutral-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-20 lg:py-28 bg-neutral-50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">
              Aplicaciones
            </h2>
            <p className="text-neutral-600">
              El sistema se integra en múltiples etapas del proceso productivo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Traslado de smolts", desc: "Conteo durante el traslado desde pisciculturas a centros de engorda." },
              { title: "Cosecha", desc: "Verificación de biomasa en operaciones de cosecha y despacho." },
              { title: "Control de mortalidad", desc: "Monitoreo continuo para registro diario de mortalidad." },
              { title: "Auditoría de inventario", desc: "Verificación de stock para reportes de producción." }
            ].map((app, index) => (
              <div key={index} className="p-6 bg-white border border-neutral-200 rounded-lg">
                <h3 className="font-medium text-neutral-900 mb-2">{app.title}</h3>
                <p className="text-sm text-neutral-600">{app.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm text-neutral-500 uppercase tracking-wide mb-4">Equipo de desarrollo</p>
            <h2 className="text-3xl font-semibold text-neutral-900 mb-6">
              Fabrimaq — Área de Inteligencia Artificial
            </h2>
            <p className="text-neutral-600 mb-10">
              Proyecto liderado por Luis Salamanca, Ingeniero en Inteligencia Artificial,
              combinando experiencia industrial con tecnología de vanguardia en computer vision y deep learning.
            </p>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-500">
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Desarrollo en Chile
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Soporte técnico local
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Solución probada
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contacto" className="py-20 lg:py-28 bg-neutral-50">
        <div className="container mx-auto px-6">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-semibold text-neutral-900 mb-4">
                Contacto
              </h2>
              <p className="text-neutral-600">
                Completa el formulario para coordinar una demostración en AquaSur 2026.
              </p>
            </div>

            <Card className="border-neutral-200 shadow-sm">
              <CardContent className="pt-6">
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="nombre" className="text-neutral-700">Nombre</Label>
                      <Input
                        id="nombre"
                        placeholder="Juan Pérez"
                        {...form.register('nombre')}
                        className={`border-neutral-300 focus:border-neutral-500 ${form.formState.errors.nombre ? 'border-red-400' : ''}`}
                      />
                      {form.formState.errors.nombre && (
                        <p className="text-red-500 text-xs">{form.formState.errors.nombre.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cargo" className="text-neutral-700">Cargo</Label>
                      <Input
                        id="cargo"
                        placeholder="Jefe de Producción"
                        {...form.register('cargo')}
                        className={`border-neutral-300 focus:border-neutral-500 ${form.formState.errors.cargo ? 'border-red-400' : ''}`}
                      />
                      {form.formState.errors.cargo && (
                        <p className="text-red-500 text-xs">{form.formState.errors.cargo.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="empresa" className="text-neutral-700">Empresa</Label>
                    <Input
                      id="empresa"
                      placeholder="Nombre de la empresa"
                      {...form.register('empresa')}
                      className={`border-neutral-300 focus:border-neutral-500 ${form.formState.errors.empresa ? 'border-red-400' : ''}`}
                    />
                    {form.formState.errors.empresa && (
                      <p className="text-red-500 text-xs">{form.formState.errors.empresa.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="correo" className="text-neutral-700">Correo electrónico</Label>
                      <Input
                        id="correo"
                        type="email"
                        placeholder="correo@empresa.cl"
                        {...form.register('correo')}
                        className={`border-neutral-300 focus:border-neutral-500 ${form.formState.errors.correo ? 'border-red-400' : ''}`}
                      />
                      {form.formState.errors.correo && (
                        <p className="text-red-500 text-xs">{form.formState.errors.correo.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="numero" className="text-neutral-700">Teléfono (opcional)</Label>
                      <Input
                        id="numero"
                        placeholder="+56 9 1234 5678"
                        {...form.register('numero')}
                        className="border-neutral-300 focus:border-neutral-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="descripcion" className="text-neutral-700">Mensaje (opcional)</Label>
                    <Textarea
                      id="descripcion"
                      placeholder="Cuéntanos sobre tu necesidad..."
                      rows={3}
                      {...form.register('descripcion')}
                      className="border-neutral-300 focus:border-neutral-500"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-neutral-900 hover:bg-neutral-800 text-white"
                    disabled={isFormSubmitting}
                  >
                    {isFormSubmitting ? "Enviando..." : "Enviar mensaje"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-neutral-200">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <p className="font-medium text-neutral-900">Fabrimaq</p>
              <p className="text-sm text-neutral-500">Sistema CV Contador de Peces</p>
            </div>
            <div className="flex items-center gap-6 text-sm text-neutral-500">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                AquaSur — Marzo 2026
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Puerto Montt
              </span>
            </div>
          </div>
          <Separator className="my-8" />
          <p className="text-center text-xs text-neutral-400">
            Proyecto dirigido por Luis Salamanca — Ingeniero en Inteligencia Artificial
          </p>
        </div>
      </footer>

      <Toaster position="top-center" />
    </div>
  );
};

export default AquaSur2025;
